import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import {
  decidirDisparos,
  gerarId,
  reconciliarProximas,
} from "../utils/agendamento/engine";
import { executarAgendamento } from "../utils/agendamento/executor";
import {
  lerDensidadeAtual,
  montarTemaExportAtual,
} from "../utils/exportacao";
import { adquirirLideranca } from "../utils/agendamento/leader";
import {
  precisaGesturePraPermissao,
  solicitarPermissao,
  statusPermissao,
  suporteFileSystemAccess,
} from "../utils/agendamento/permissions";
import {
  ehChaveDoAgendador,
  lerConfig,
  lerDirectoryHandle,
  lerHistorico,
  registrarExecucao,
  removerDirectoryHandle,
  salvarConfig,
  salvarDirectoryHandle,
} from "../utils/agendamento/storage";
import { obterLabel } from "../utils/painelRegistry";

const AgendamentoContext = createContext(null);

const TICK_MS = 30 * 1000;

export function AgendamentoProvider({ children }) {
  const toast = useToast();
  // PJ — 2026-05-22: passa o request autenticado para o executor.
  // Permite warm-up do token + acordar Render free antes de cada iframe
  // oculto, evitando o redirect /login que quebrava 100% das exportações
  // JPG/PNG agendadas.
  const { request: authRequest } = useAuth();

  const [config, setConfig] = useState(() => lerConfig());
  const [historico, setHistorico] = useState(() => lerHistorico());
  const [handle, setHandle] = useState(null);
  const [permissao, setPermissao] = useState("indisponivel");
  const [lider, setLider] = useState(false);
  const [executando, setExecutando] = useState(false);
  const [nomeDiretorio, setNomeDiretorio] = useState("");

  const suportado = useMemo(() => suporteFileSystemAccess(), []);
  const filaExecucoes = useRef([]);
  const executandoRef = useRef(false);
  // Serializa tick, fila de permissão e execução manual numa única cadeia de
  // Promises (mesmo padrão do refreshSession do AuthContext): sem isso, um
  // tick que caia na janela assíncrona de statusPermissao passava o guard e
  // executava o MESMO agendamento em paralelo (dois iframes, dois arquivos).
  const cadeiaExecucaoRef = useRef(Promise.resolve());
  // Toda porta de entrada de execução passa por aqui: tick, drenagem da fila e
  // o botão "Executar agora". Serializar só o tick deixava as outras duas
  // correndo em paralelo com o lote agendado.
  const serializar = useCallback((fn) => {
    cadeiaExecucaoRef.current = cadeiaExecucaoRef.current.then(
      fn,
      (erroAnterior) => {
        // O elo anterior falhou: registra e segue a fila. Sem isto, o motivo
        // do erro era descartado silenciosamente.
        console.error("[agendamento] execução anterior falhou:", erroAnterior);
        return fn();
      }
    );
    return cadeiaExecucaoRef.current;
  }, []);
  // O tick imediato do boot pode rodar antes do IndexedDB devolver o handle
  // persistido — sem esta flag, agendamento vencido gerava toast falso de
  // "nenhuma pasta selecionada" toda manhã.
  const handleCarregadoRef = useRef(false);
  const configRef = useRef(config);
  const handleRef = useRef(null);

  useEffect(() => {
    configRef.current = config;
  }, [config]);
  useEffect(() => {
    handleRef.current = handle;
  }, [handle]);

  // Carrega handle persistido no mount.
  useEffect(() => {
    if (!suportado) return;
    (async () => {
      try {
        const h = await lerDirectoryHandle();
        if (h) {
          setHandle(h);
          setNomeDiretorio(h.name || "");
          const status = await statusPermissao(h);
          setPermissao(status);
        }
      } finally {
        // Mesmo com handle nulo/falha: o erro legítimo de "usuário nunca
        // escolheu pasta" continua aparecendo — só não antes da leitura.
        handleCarregadoRef.current = true;
      }
    })();
  }, [suportado]);

  // Espelha o que outra aba gravou. Sem isto, a aba NÃO-líder mostrava o
  // histórico congelado do momento em que foi aberta e um "Próxima" que já
  // passou — e, pior, o próximo clique dela (pausar, editar) gravava a config
  // inteira a partir desse snapshot velho, apagando o `ultimaExecucao` que a
  // líder tinha acabado de registrar: o agendamento voltava a "nunca
  // executou" e exportava de novo no tick seguinte.
  useEffect(() => {
    function aoMudarNoutraAba(evento) {
      if (!ehChaveDoAgendador(evento.key)) return;
      const nova = lerConfig();
      configRef.current = nova;
      setConfig(nova);
      setHistorico(lerHistorico());
    }
    window.addEventListener("storage", aoMudarNoutraAba);
    return () => window.removeEventListener("storage", aoMudarNoutraAba);
  }, []);

  // Eleição de aba líder.
  useEffect(() => {
    if (!suportado) return;
    const liberar = adquirirLideranca(
      () => setLider(true),
      () => setLider(false)
    );
    return () => liberar();
  }, [suportado]);

  // CRUD ---------------------------------------------------------------------

  const persistir = useCallback((novaConfig) => {
    const recon = {
      ...novaConfig,
      agendamentos: reconciliarProximas(novaConfig.agendamentos || []),
    };
    salvarConfig(recon);
    // Síncrono, ANTES do setConfig: o efeito que sincroniza configRef só roda
    // depois do render, e nesse meio-tempo um tick lê a config velha — decide
    // disparo por ultimaExecucao desatualizada e repete a exportação.
    configRef.current = recon;
    setConfig(recon);
    return recon;
  }, []);

  const criarAgendamento = useCallback(
    (dados) => {
      const novo = {
        id: gerarId(),
        nome: dados.nome || "Sem nome",
        paineis: dados.paineis || [],
        formatos: dados.formatos || ["pdf"],
        frequencia: dados.frequencia || { tipo: "diario", hora: 8, minuto: 0 },
        subpasta: dados.subpasta || "",
        ativo: dados.ativo !== false,
        ocultarPrecos: Boolean(dados.ocultarPrecos),
        criadoEm: new Date().toISOString(),
        ultimaExecucao: null,
        proximaExecucao: null,
      };
      persistir({
        ...configRef.current,
        agendamentos: [...(configRef.current.agendamentos || []), novo],
      });
      return novo;
    },
    [persistir]
  );

  const atualizarAgendamento = useCallback(
    (id, patch) => {
      const novos = (configRef.current.agendamentos || []).map((a) =>
        a.id === id ? { ...a, ...patch } : a
      );
      persistir({ ...configRef.current, agendamentos: novos });
    },
    [persistir]
  );

  const removerAgendamento = useCallback(
    (id) => {
      const novos = (configRef.current.agendamentos || []).filter((a) => a.id !== id);
      persistir({ ...configRef.current, agendamentos: novos });
    },
    [persistir]
  );

  const togglePausarTodos = useCallback(() => {
    persistir({ ...configRef.current, pausarTodos: !configRef.current.pausarTodos });
  }, [persistir]);

  // Pasta de destino ---------------------------------------------------------

  const escolherPasta = useCallback(async () => {
    if (!suportado) {
      toast.error("Seu navegador não suporta seleção de pasta. Use Chrome ou Edge desktop.");
      return null;
    }
    try {
      const h = await window.showDirectoryPicker({ mode: "readwrite" });
      await salvarDirectoryHandle(h);
      setHandle(h);
      setNomeDiretorio(h.name || "");
      const status = await statusPermissao(h);
      setPermissao(status);
      toast.success(`Pasta "${h.name}" selecionada para exportações automáticas.`);
      return h;
    } catch (e) {
      // AbortError = usuário cancelou o picker.
      if (e?.name !== "AbortError") {
        toast.error(`Erro ao selecionar pasta: ${e?.message || e}`);
      }
      return null;
    }
  }, [suportado, toast]);

  const removerPasta = useCallback(async () => {
    await removerDirectoryHandle();
    setHandle(null);
    setNomeDiretorio("");
    setPermissao("indisponivel");
    toast.info("Pasta de destino removida. Exportações agendadas estão pausadas.");
  }, [toast]);

  // Ref para `processarFila` — permite que `reativarPermissao` a chame sem
  // precisar declará-la como dependência (que criaria ciclo:
  // executar → reativarPermissao → processarFila → executar). A ref é
  // atualizada via useEffect logo após a declaração de `processarFila`.
  const processarFilaRef = useRef(null);

  const reativarPermissao = useCallback(async () => {
    if (!handleRef.current) return;
    const status = await solicitarPermissao(handleRef.current);
    setPermissao(status);
    if (status === "granted") {
      toast.success("Permissão concedida. Agendamentos retomados.");
      // Drena fila se houve disparos enfileirados — pela MESMA cadeia do
      // tick, senão a drenagem corre em paralelo com um lote em andamento.
      serializar(() => processarFilaRef.current?.() ?? Promise.resolve());
    } else if (status === "denied") {
      toast.error("Permissão negada. Selecione novamente a pasta.");
    }
  }, [serializar, toast]);

  // Execução -----------------------------------------------------------------

  const executar = useCallback(
    async (agendamento) => {
      // Guard no TOPO: executandoRef só virava true depois do await de
      // permissão, e nessa janela um tick reentrante executava o mesmo
      // agendamento em paralelo (dois iframes, dois arquivos).
      if (executandoRef.current) return;
      executandoRef.current = true;
      const handleAtual = handleRef.current;
      if (!handleAtual) {
        toast.error(
          `Não consigo executar "${agendamento.nome}" — nenhuma pasta de destino selecionada.`,
          { dedupKey: "agendamento-sem-pasta" }
        );
        executandoRef.current = false;
        return;
      }
      const status = await statusPermissao(handleAtual);
      setPermissao(status);
      if (status !== "granted") {
        if (precisaGesturePraPermissao(status)) {
          // Enfileira; usuário deve clicar no badge para conceder. Dedup por
          // id — cada tick de 30s re-decidia os mesmos disparos e empilhava
          // cópias: 1h de espera = ~120 execuções repetidas ao reativar.
          if (!filaExecucoes.current.some((a) => a.id === agendamento.id)) {
            filaExecucoes.current.push(agendamento);
          }
          toast.warning(
            "Permissão expirada — clique aqui para reativar agendamentos.",
            {
              sticky: true,
              dedupKey: "agendamento-permissao-expirada",
              actionLabel: "Reativar",
              onAction: reativarPermissao,
            }
          );
          executandoRef.current = false;
          return;
        }
        toast.error("Permissão indisponível. Selecione nova pasta.", {
          dedupKey: "agendamento-permissao-indisponivel",
        });
        executandoRef.current = false;
        return;
      }
      setExecutando(true);
      const inicio = new Date().toISOString();
      try {
        const resultados = await executarAgendamento(agendamento, {
          handle: handleAtual,
          warmupRequest: authRequest,
          // Resolvidos AGORA, na aba principal, que é a fonte da verdade da
          // aparência. O iframe oculto NÃO herda isso sozinho: ele faz um boot
          // próprio e pode subir claro com a aba escura (era o motivo de um
          // mesmo lote sair com temas diferentes). Quem alinha os dois é o
          // espelhamento em agendamento/iframeCapture.js.
          tema: montarTemaExportAtual(),
          densidade: lerDensidadeAtual(),
        });
        const fim = new Date().toISOString();
        const sucessos = resultados.filter((r) => r.status === "ok");
        const erros = resultados.filter((r) => r.status === "erro");
        const pulados = resultados.filter((r) => r.status === "pulado");

        // Registrar UMA entrada por item gerado (visibilidade).
        for (const r of resultados) {
          registrarExecucao({
            id: gerarId(),
            agendamentoId: agendamento.id,
            agendamentoNome: agendamento.nome,
            painelId: r.painelId,
            painelLabel: obterLabel(r.painelId),
            formato: r.formato,
            status: r.status,
            inicio,
            fim,
            arquivoNome: r.arquivoNome || null,
            erro: r.erro || null,
            aviso: r.aviso || null,
          });
        }
        setHistorico(lerHistorico());

        // Atualiza ultima/proxima execução.
        atualizarAgendamento(agendamento.id, {
          ultimaExecucao: fim,
        });

        if (sucessos.length && !erros.length && !pulados.length) {
          toast.success(
            `Exportação "${agendamento.nome}" concluída — ${sucessos.length} arquivo(s).`
          );
        } else if (sucessos.length) {
          toast.warning(
            `Exportação "${agendamento.nome}" concluída parcialmente: ${sucessos.length} ok, ${pulados.length} pulado(s), ${erros.length} erro(s).`
          );
        } else {
          toast.error(
            `Exportação "${agendamento.nome}" falhou: ${pulados.length} pulado(s), ${erros.length} erro(s).`
          );
        }
      } catch (e) {
        toast.error(`Falha ao executar "${agendamento.nome}": ${e?.message || e}`);
      } finally {
        setExecutando(false);
        executandoRef.current = false;
      }
    },
    [atualizarAgendamento, authRequest, reativarPermissao, toast]
  );

  const processarFila = useCallback(async () => {
    // Drena um SNAPSHOT da fila: se `executar` re-enfileirar o item (a
    // permissao voltou a "prompt" no meio da drenagem), o while sobre a fila
    // viva reencontrava o mesmo item para sempre — laco assincrono infinito,
    // visivel so como o notebook esquentando (o toast e deduplicado).
    const paraExecutar = filaExecucoes.current.splice(0);
    for (const ag of paraExecutar) {
      if (executandoRef.current) break;
      // Re-resolve pelo estado ATUAL: o item pode ter sido pausado, editado
      // ou removido enquanto esperava o usuário reativar a permissão.
      const atual = (configRef.current.agendamentos || []).find(
        (a) => a.id === ag.id
      );
      if (!atual || !atual.ativo || configRef.current.pausarTodos) continue;
      // eslint-disable-next-line no-await-in-loop
      await executar(atual);
    }
  }, [executar]);

  // Mantém `processarFilaRef.current` sincronizado com a versão atual
  // de `processarFila`. Quebra o ciclo de dependências do useCallback
  // (reativarPermissao não precisa listar processarFila como dep).
  useEffect(() => {
    processarFilaRef.current = processarFila;
  }, [processarFila]);

  // Tick principal ----------------------------------------------------------

  useEffect(() => {
    if (!suportado || !lider) return undefined;
    let cancelado = false;
    const tick = async () => {
      if (cancelado || executandoRef.current) return;
      // Handle ainda carregando do IndexedDB: só espera o próximo tick
      // (<=30s). NÃO enfileira — a fila só drena via "Reativar" e os itens
      // ficariam presos; decidirDisparos re-detecta o atraso sozinho.
      if (!handleCarregadoRef.current) return;
      const disparos = decidirDisparos(configRef.current, new Date());
      if (disparos.length === 0) return;

      // Check de permissão UMA vez por tick — evita N toasts duplicados
      // quando N agendamentos disparam simultaneamente com permissão
      // expirada. Cada toast tem `dedupKey` como defesa extra.
      const handleAtual = handleRef.current;
      if (handleAtual) {
        const status = await statusPermissao(handleAtual);
        if (cancelado) return;
        setPermissao(status);
        if (status !== "granted") {
          // Enfileira tudo para drenagem quando o usuário reativar — com
          // dedup por id (ver comentário no caminho manual).
          for (const { agendamento } of disparos) {
            if (!filaExecucoes.current.some((a) => a.id === agendamento.id)) {
              filaExecucoes.current.push(agendamento);
            }
          }
          if (precisaGesturePraPermissao(status)) {
            toast.warning(
              "Permissão expirada — clique aqui para reativar agendamentos.",
              {
                sticky: true,
                dedupKey: "agendamento-permissao-expirada",
                actionLabel: "Reativar",
                onAction: reativarPermissao,
              }
            );
          } else {
            toast.error("Permissão indisponível. Selecione nova pasta.", {
              dedupKey: "agendamento-permissao-indisponivel",
            });
          }
          return;
        }
      }

      for (const { agendamento } of disparos) {
        if (cancelado) return;
        // eslint-disable-next-line no-await-in-loop
        await executar(agendamento);
      }
    };
    // Serializa cada tick na cadeia única de execução (ver cadeiaExecucaoRef).
    const tickSerializado = () => serializar(tick);
    // Dispara um imediato (cobre atrasados ao reabrir).
    tickSerializado();
    const handle = setInterval(tickSerializado, TICK_MS);
    return () => {
      cancelado = true;
      clearInterval(handle);
    };
  }, [executar, lider, reativarPermissao, serializar, suportado, toast]);

  // Re-checa status de permissão periodicamente quando há handle.
  useEffect(() => {
    if (!handle) return undefined;
    let cancelado = false;
    const check = async () => {
      const status = await statusPermissao(handle);
      if (!cancelado) setPermissao(status);
    };
    check();
    const id = setInterval(check, 60 * 1000);
    return () => {
      cancelado = true;
      clearInterval(id);
    };
  }, [handle]);

  // Quando a permissão volta a "granted", limpa toasts pendentes de
  // permissão expirada/indisponível — usuário não precisa fechar manualmente.
  useEffect(() => {
    if (permissao === "granted") {
      toast.removerPorDedupKey("agendamento-permissao-expirada");
      toast.removerPorDedupKey("agendamento-permissao-indisponivel");
    }
    // toast.removerPorDedupKey é uma referencia estavel (useCallback no
    // ToastProvider). Listamos como dep para satisfazer exhaustive-deps.
  }, [permissao, toast]);

  // API ---------------------------------------------------------------------

  const api = useMemo(
    () => ({
      suportado,
      lider,
      executando,
      handle,
      nomeDiretorio,
      permissao,
      config,
      historico,
      criarAgendamento,
      atualizarAgendamento,
      removerAgendamento,
      togglePausarTodos,
      escolherPasta,
      removerPasta,
      reativarPermissao,
      // O botão "Executar agora" compartilha a cadeia: nunca roda junto com o
      // tick nem com a drenagem da fila.
      executar: (ag) => serializar(() => executar(ag)),
      recarregarHistorico: () => setHistorico(lerHistorico()),
    }),
    [
      suportado,
      lider,
      executando,
      handle,
      nomeDiretorio,
      permissao,
      config,
      historico,
      criarAgendamento,
      atualizarAgendamento,
      removerAgendamento,
      togglePausarTodos,
      escolherPasta,
      removerPasta,
      reativarPermissao,
      executar,
      serializar,
    ]
  );

  return (
    <AgendamentoContext.Provider value={api}>{children}</AgendamentoContext.Provider>
  );
}

export function useAgendamento() {
  const ctx = useContext(AgendamentoContext);
  if (!ctx) {
    // Fallback inerte — útil em testes sem provider.
    return {
      suportado: false,
      lider: false,
      executando: false,
      handle: null,
      nomeDiretorio: "",
      permissao: "indisponivel",
      config: { pausarTodos: false, agendamentos: [] },
      historico: [],
      criarAgendamento: () => null,
      atualizarAgendamento: () => {},
      removerAgendamento: () => {},
      togglePausarTodos: () => {},
      escolherPasta: () => Promise.resolve(null),
      removerPasta: () => Promise.resolve(),
      reativarPermissao: () => Promise.resolve(),
      executar: () => Promise.resolve(),
      recarregarHistorico: () => {},
    };
  }
  return ctx;
}
